import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL;

export interface StandingTask {
  id: number;
  taskId: number;
  taskNumber: string;
  type: 'Routine' | 'Quantified' | 'Overhead';
  mode?: 'standing';
  name: string;
  description?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  activities: Array<{
    id: number;
    activityName: string;
    activityNumber: string;
    unitOfMeasure: string;
  }>;
  foremanArea?: {
    id: number;
    name: string;
    serviceArea: {
      id: number;
      name: string;
    };
  };
  project?: {
    id: number;
    name: string;
    serviceArea: {
      id: number;
      name: string;
    };
  };
  createdBy: {
    id: number;
    name: string;
    lastName: string;
  };
  routineTasks?: Array<{
    id: number;
    labourAllocation: number;
    materialAllocation: number;
    equipmentAllocation: number;
    secondaryLabourAllocation: number;
    secondaryMaterialAllocation: number;
    secondaryEquipmentAllocation: number;
    taskSubType: string | null;
  }>;
  quantifiedTasks?: Array<{
    id: number;
    identifiedQuantity: number;
    unitOfMeasure: string;
  }>;
  taskCostings?: Array<{
    id: number;
    baseRate: number;
    rateMultiplier: number;
    rateAdjustment: number;
    totalValue: number;
    taskType: string;
    costType: string;
    rateAdjustmentComments: string | null;
    labourMarkup: number;
    materialMarkup: number;
    subcontractorMarkup: number;
    vehicleMarkup: number;
    hiredEquipmentMarkup: number;
  }>;
}

export interface StandingTaskFilters {
  type?: string;
  status?: string[];
  serviceArea?: string;
  activityName?: string[];
  search?: string;
  /** Server-side sort (allowlisted): taskNumber, name, type, status, createdBy. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface StandingTaskListResponse {
  data: StandingTask[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface StandingTaskFilterOptions {
  serviceAreas: Array<{ id: number; name: string }>;
  activities: Array<{ activityName: string; activityNumber: string }>;
  statuses: string[];
  types: string[];
}

export interface CreateQuantifiedTaskDTO {
  id?: number;
  taskNumber?: string;
  name: string;
  activityId: number;
  description?: string;
  type: 'Quantified';
  status: string;
}

export interface CreateRoutineTaskDTO {
  id?: number;
  taskNumber?: string;
  name: string;
  activityId: number;
  secondaryActivityId?: number;
  description?: string;
  type: 'Routine';
  status: string;
  foremanAreaId?: number;
  labourAllocation?: number;
  materialAllocation?: number;
  equipmentAllocation?: number;
  secondaryLabourAllocation?: number;
  secondaryMaterialAllocation?: number;
  secondaryEquipmentAllocation?: number;
  taskSubType?: string;
}

export interface CreateOverheadTaskDTO {
  id?: number;
  taskNumber?: string;
  name: string;
  activityId: number;
  description?: string;
  type: 'Overhead';
  status: string;
}

class StandingTaskService {
  // Generic methods for all standing tasks
  async getAllStandingTasks(): Promise<StandingTask[]> {
    try {
      const response = await axios.get(`${BASE_URL}/tasks/standing-task`, {
        params: { limit: 1000 } // Get all tasks by setting a high limit
      });
      // Handle both old format (array) and new format (object with data property)
      return Array.isArray(response.data) ? response.data : response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getStandingTasksByType(type: 'Routine' | 'Quantified' | 'Overhead'): Promise<StandingTask[]> {
    try {
      const response = await axios.get(`${BASE_URL}/tasks/standing-task`, {
        params: { type, limit: 1000 }
      });
      // Handle both old format (array) and new format (object with data property)
      return Array.isArray(response.data) ? response.data : response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getStandingTasksPaginated(
    page: number,
    limit: number,
    filters?: StandingTaskFilters
  ): Promise<StandingTaskListResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.set('page', String(page));
      queryParams.set('limit', String(limit));

      if (filters) {
        if (filters.type) queryParams.set('type', filters.type);
        if (filters.serviceArea) queryParams.set('serviceArea', filters.serviceArea);
        if (filters.search) queryParams.set('search', filters.search);
        if (filters.status && filters.status.length > 0) {
          queryParams.set('status', filters.status.join(','));
        }
        if (filters.activityName && filters.activityName.length > 0) {
          for (const name of filters.activityName) {
            const trimmed = String(name).trim();
            if (trimmed) queryParams.append('activityName', trimmed);
          }
        }
        if (filters.sortBy) queryParams.set('sortBy', filters.sortBy);
        if (filters.sortOrder) queryParams.set('sortOrder', filters.sortOrder);
      }

      const qs = queryParams.toString();
      const response = await axios.get(
        `${BASE_URL}/tasks/standing-task${qs ? `?${qs}` : ''}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getFilterOptions(): Promise<StandingTaskFilterOptions> {
    try {
      const response = await axios.get(`${BASE_URL}/tasks/standing-task/filter-options`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Overhead task specific methods
  async createOverheadTask(taskData: CreateOverheadTaskDTO): Promise<StandingTask> {
    try {
      const response = await axios.post(`${BASE_URL}/tasks/standing-task/overhead`, taskData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateOverheadTask(taskId: number, taskData: CreateOverheadTaskDTO): Promise<StandingTask> {
    try {
      const response = await axios.put(`${BASE_URL}/tasks/standing-task/overhead/${taskId}`, taskData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteStandingTask(taskId: number): Promise<void> {
    try {
      await axios.delete(`${BASE_URL}/tasks/standing-task/${taskId}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Quantified task specific methods
  async createQuantifiedTask(taskData: CreateQuantifiedTaskDTO): Promise<StandingTask> {
    try {
      const response = await axios.post(`${BASE_URL}/tasks/standing-task/quantified`, taskData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateQuantifiedTask(taskId: number, taskData: CreateQuantifiedTaskDTO): Promise<StandingTask> {
    try {
      const response = await axios.put(`${BASE_URL}/tasks/standing-task/quantified/${taskId}`, taskData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Routine task specific methods
  async createRoutineTask(taskData: CreateRoutineTaskDTO): Promise<StandingTask> {
    try {
      const response = await axios.post(`${BASE_URL}/tasks/standing-task/`, taskData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateRoutineTask(taskId: number, taskData: CreateRoutineTaskDTO): Promise<StandingTask> {
    try {
      const response = await axios.put(`${BASE_URL}/tasks/standing-task/routine/${taskId}`, taskData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: any): Error {
    console.error('API Error:', error);
    if (error.response) {
      const errorMessage = error.response.data.details || error.response.data.message || 'An error occurred';
      console.error('Error details:', errorMessage);
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error('No response received from server');
    } else {
      throw new Error('Error setting up request');
    }
  }
}

export const standingTaskService = new StandingTaskService();
export default standingTaskService; 